import * as ImagePicker from 'expo-image-picker';
import * as FileSystem from 'expo-file-system';
import { Platform } from 'react-native';
import api from './api';

// expo-image-manipulator nur auf nativen Plattformen importieren (nicht im Web)
let ImageManipulator = null;
if (Platform.OS !== 'web') {
  // Native Plattform (iOS/Android) - dynamisch importieren
  try {
    ImageManipulator = require('expo-image-manipulator');
  } catch (err) {
    console.warn('expo-image-manipulator nicht verfügbar:', err);
  }
}

// HEIC-Konvertierung nur im Web importieren
let heic2any = null;
let heic2anyLoading = false;
let heic2anyPromise = null;

// Lade heic2any beim ersten Aufruf
const loadHeic2any = async () => {
  if (typeof window === 'undefined') {
    return null;
  }
  
  if (heic2any) {
    return heic2any;
  }
  
  if (heic2anyLoading && heic2anyPromise) {
    return heic2anyPromise;
  }
  
  heic2anyLoading = true;
  heic2anyPromise = import('heic2any')
    .then(module => {
      heic2any = module.default;
      heic2anyLoading = false;
      return heic2any;
    })
    .catch(err => {
      console.warn('heic2any nicht verfügbar:', err);
      heic2anyLoading = false;
      heic2anyPromise = null;
      return null;
    });
  
  return heic2anyPromise;
};

// Berechtigungen für Bildauswahl anfordern
export const requestImagePermissions = async () => {
  try {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      throw new Error('Berechtigung für Galerie-Zugriff verweigert');
    }
  } catch (error) {
    console.error('Permission request error:', error);
    // Im Web ignorieren wir Berechtigungsfehler
    if (typeof window !== 'undefined') {
      console.log('Web environment: ignoring permission error');
      return;
    }
    throw error;
  }
};

// Berechtigungen für Kamera anfordern
export const requestCameraPermissions = async () => {
  const { status } = await ImagePicker.requestCameraPermissionsAsync();
  if (status !== 'granted') {
    throw new Error('Berechtigung für Kamera-Zugriff verweigert');
  }
};

// Bild aus Galerie auswählen
export const pickImageFromGallery = async () => {
  try {
    await requestImagePermissions();
    
    // WICHTIG: Auf iOS konvertiert expo-image-picker HEIC automatisch zu JPEG,
    // wenn quality gesetzt ist. Wir setzen quality auf 0.8 für bessere Qualität
    // und stellen sicher, dass allowsEditing true ist, damit die Konvertierung funktioniert.
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.8, // Qualität erhöht für bessere Konvertierung von HEIC zu JPEG auf iOS
      // Akzeptiere alle Bildformate, auch HEIC
      allowsMultipleSelection: false,
    });

    if (result.canceled) {
      return null;
    }

    const asset = result.assets[0];
    
    // Prüfe ob es eine HEIC-Datei ist (in Web-Version)
    if (typeof window !== 'undefined' && asset.uri) {
      const lowerUri = asset.uri.toLowerCase();
      if (lowerUri.includes('.heic') || lowerUri.includes('.heif')) {
        console.log('HEIC-Datei ausgewählt - wird beim Upload automatisch konvertiert');
      }
    } else {
      // Auf iOS: expo-image-picker sollte HEIC automatisch zu JPEG konvertieren
      // wenn quality gesetzt ist. Prüfe ob das Bild HEIC ist.
      if (asset.uri && (asset.uri.toLowerCase().includes('.heic') || asset.uri.toLowerCase().includes('.heif'))) {
        console.log('HEIC-Datei auf iOS erkannt - sollte automatisch zu JPEG konvertiert werden');
      }
    }

    return asset;
  } catch (error) {
    console.error('Gallery picker error:', error);
    throw error;
  }
};

// Bild mit Kamera aufnehmen
export const takePhotoWithCamera = async () => {
  await requestCameraPermissions();
  
  // WICHTIG: Auf iOS konvertiert expo-image-picker HEIC automatisch zu JPEG,
  // wenn quality gesetzt ist. Wir setzen quality auf 0.8 für bessere Qualität.
  const result = await ImagePicker.launchCameraAsync({
    allowsEditing: true,
    aspect: [1, 1],
    quality: 0.8, // Qualität erhöht für bessere Konvertierung von HEIC zu JPEG auf iOS
  });

  if (result.canceled) {
    return null;
  }

  return result.assets[0];
};

// Bild als Base64 konvertieren
export const convertImageToBase64 = async (imageUri) => {
  try {
    if (typeof window !== 'undefined' && window.navigator) {
      // Web: Fetch image and convert to base64
      const response = await fetch(imageUri);
      let blob = await response.blob();
      
      // iOS Safari: Automatische Größenreduzierung bei großen Bildern
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS && blob.size > 500000) { // > 500KB
        // Erstelle Canvas für Größenreduzierung
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        await new Promise((resolve, reject) => {
          img.onload = () => {
            // Berechne neue Größe (max 500KB)
            const maxSize = 500000;
            const ratio = Math.sqrt(maxSize / blob.size);
            const newWidth = Math.floor(img.width * ratio);
            const newHeight = Math.floor(img.height * ratio);
            
            canvas.width = newWidth;
            canvas.height = newHeight;
            ctx.drawImage(img, 0, 0, newWidth, newHeight);
            
            canvas.toBlob((newBlob) => {
              blob = newBlob;
              resolve();
            }, 'image/jpeg', 0.7);
          };
          img.onerror = reject;
          img.src = imageUri;
        });
      }
      
      // Prüfe ob es eine HEIC-Datei ist und konvertiere sie
      const lowerUri = imageUri.toLowerCase();
      const isHeic = blob.type === 'image/heic' || 
                     blob.type === 'image/heif' || 
                     lowerUri.includes('.heic') || 
                     lowerUri.includes('.heif') ||
                     (blob.type === '' && (lowerUri.includes('.heic') || lowerUri.includes('.heif'))); // Manchmal ist blob.type leer bei HEIC-Dateien
      
      if (isHeic) {
        console.log('HEIC-Datei erkannt, konvertiere zu JPEG...');
        
        // Lade heic2any falls noch nicht geladen (mit Timeout)
        const heicConverter = await Promise.race([
          loadHeic2any(),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('HEIC-Konverter konnte nicht geladen werden')), 10000)
          )
        ]).catch(err => {
          console.error('Fehler beim Laden von heic2any:', err);
          return null;
        });
        
        if (heicConverter) {
          try {
            // heic2any gibt ein Array zurück, auch bei einem einzelnen Bild
            // Timeout für die Konvertierung (30 Sekunden)
            const convertedBlob = await Promise.race([
              heicConverter({
                blob: blob,
                toType: 'image/jpeg',
                quality: 0.7
              }),
              new Promise((_, reject) => 
                setTimeout(() => reject(new Error('HEIC-Konvertierung hat zu lange gedauert')), 30000)
              )
            ]);
            
            // Wenn Array zurückgegeben wird, nimm das erste Element
            blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
            console.log('HEIC erfolgreich zu JPEG konvertiert');
          } catch (conversionError) {
            console.error('HEIC-Konvertierungsfehler:', conversionError);
            throw new Error('HEIC-Format wird nicht unterstützt. Bitte verwende JPG, PNG oder konvertiere das Bild vorher.');
          }
        } else {
          throw new Error('HEIC-Format wird nicht unterstützt. Bitte verwende JPG oder PNG.');
        }
      }
      
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result.split(',')[1]; // Remove data:image/jpeg;base64, prefix
          const mimeType = reader.result.split(',')[0].split(':')[1].split(';')[0];
          const base64Length = base64.length;
          
          // iOS Safari hat sehr strenge Limits für Base64-Data-URLs
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
          const maxSize = isIOS ? 500000 : 1000000; // 500KB für iOS, 1MB für andere
          
          if (base64Length > maxSize) {
            if (isIOS) {
              throw new Error(`Bild zu groß für iOS Safari (${Math.round(base64Length / 1024)}KB > ${Math.round(maxSize / 1024)}KB). Bitte wählen Sie ein kleineres Bild.`);
            }
          }
          
          resolve(`data:${mimeType};base64,${base64}`);
        };
        reader.onerror = (error) => {
          reject(error);
        };
        reader.readAsDataURL(blob);
      });
    } else {
      // Native (iOS/Android): Use FileSystem to read and convert
      // WICHTIG: Auf iOS konvertiert expo-image-picker HEIC nicht immer automatisch.
      // Wir verwenden expo-image-manipulator, um HEIC zu JPEG zu konvertieren.
      const lowerUri = imageUri.toLowerCase();
      const isHeic = lowerUri.includes('.heic') || lowerUri.includes('.heif');
      
      let finalUri = imageUri;
      
      // Wenn HEIC erkannt wird, konvertiere es zu JPEG mit expo-image-manipulator
      if (isHeic && ImageManipulator) {
        try {
          console.log('HEIC-Datei auf iOS erkannt - konvertiere zu JPEG...');
          const manipulatedImage = await ImageManipulator.manipulateAsync(
            imageUri,
            [], // Keine Transformationen, nur Konvertierung
            {
              compress: 0.8,
              format: ImageManipulator.SaveFormat.JPEG,
            }
          );
          finalUri = manipulatedImage.uri;
          console.log('HEIC erfolgreich zu JPEG konvertiert');
        } catch (conversionError) {
          console.error('HEIC-Konvertierungsfehler auf iOS:', conversionError);
          // Falls Konvertierung fehlschlägt, versuche es trotzdem als JPEG zu behandeln
          console.warn('Konvertierung fehlgeschlagen, versuche Bild trotzdem zu laden');
        }
      } else if (isHeic && !ImageManipulator) {
        console.warn('HEIC-Datei erkannt, aber expo-image-manipulator nicht verfügbar - versuche Bild trotzdem zu laden');
      }
      
      const base64 = await FileSystem.readAsStringAsync(finalUri, {
        encoding: 'base64',
      });
      
      // Auf iOS sollte das Bild jetzt JPEG sein
      return `data:image/jpeg;base64,${base64}`;
    }
  } catch (error) {
    console.error('Base64 conversion error:', error);
    throw new Error(error.message || 'Fehler beim Konvertieren des Bildes');
  }
};

// Bild zum Server hochladen (als Base64)
export const uploadImage = async (imageUri) => {
  try {
    const base64Image = await convertImageToBase64(imageUri);
    
    // Sende Base64-String direkt an Backend
    const uploadResponse = await api.post('/image', {
      image: base64Image
    });

    return uploadResponse.data;
  } catch (error) {
    console.error('Upload error:', error);
    throw new Error('Fehler beim Hochladen des Bildes');
  }
};

// Komplette Bildauswahl und Upload-Funktion
export const selectAndUploadImage = async (source = 'gallery') => {
  try {
    let imageAsset;
    
    if (source === 'camera') {
      imageAsset = await takePhotoWithCamera();
    } else {
      imageAsset = await pickImageFromGallery();
    }

    if (!imageAsset) {
      return null;
    }

    // Bild hochladen
    const uploadResult = await uploadImage(imageAsset.uri);
    
    return {
      success: true,
      imageUrl: uploadResult.imageUrl,
      filename: uploadResult.filename
    };
  } catch (error) {
    console.error('Image selection/upload error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};